import { defaultPanelBorder } from './constants.js';

/**
 * Whether the value is a strict #rrggbb hex color (defense in depth on top of server validation —
 * only ever inlined into style properties, never markup).
 * @param {unknown} value - Candidate accent color from the config payload
 * @returns {boolean}
 */
export function isHexColor(value) {
    return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
}

/**
 * Readable icon color for the given accent — client-side mirror of the server's
 * WidgetAccentContrast (WCAG relative luminance on linearized sRGB, 0.4 threshold),
 * so a white or very light accent never renders white-on-white sparkles.
 * @param {string} accent - Validated #rrggbb accent color
 * @returns {string}
 */
export function contrastFor(accent) {
    function linearize(channel) {
        const c = channel / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    }

    const rgb = parseInt(accent.slice(1), 16);
    const luminance =
        0.2126 * linearize((rgb >> 16) & 0xFF) +
        0.7152 * linearize((rgb >> 8) & 0xFF) +
        0.0722 * linearize(rgb & 0xFF);

    return luminance > 0.4 ? '#111111' : '#ffffff';
}

/**
 * Whether the visitor's OS asks for a dark look — how 'auto' is resolved for the launcher. The bubbles
 * beside it answer the same question in CSS (leadwin-nudge.css); the button carries inline styles, so
 * it has to ask here.
 * @returns {boolean}
 */
function prefersDark() {
    try {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch {
        return false; // No matchMedia: the light look is the safe assumption on an unknown page.
    }
}

/**
 * Resolves a theme setting to the look it actually shows: only 'auto' asks the visitor's OS.
 * Everything else that is not 'dark' — 'light', and 'api' before the host page has called — is light:
 * light is the API scheme's stated fallback, and the OS preference is exactly what it opted out of.
 * @param {string} theme - 'dark' | 'light' | 'auto' | 'api'
 * @returns {boolean}
 */
function isDarkTheme(theme) {
    return theme === 'dark' || (theme === 'auto' && prefersDark());
}

/**
 * The panel's chrome for the look it stands in: the outline that keeps it a pane, the shadow that
 * lifts it off the page, and the ground the iframe is mounted on.
 *
 * The shadow does follow the look — a dark drop shadow on a dark page is dark on dark and says
 * nothing, so there it turns into a light halo instead. What it does not do is keep the geometry: a
 * drop shadow is offset downward because the light comes from above, and light coming from above
 * cannot pool UNDER an object. The inverse of a shadow is a glow, so the dark one loses the offset
 * and surrounds the pane evenly.
 *
 * That is depth, not an edge. A 40px blur has no boundary anywhere, least of all along the top where
 * a shadow leaves off — which is why the outline stays and does the separating: a light line, or the
 * soft variant of the accent (WidgetAccentPalette.Tint) where the contractor brought a colour, so the
 * edge belongs to the widget rather than being a neutral grey borrowed from nobody. Light pages need
 * no line at all; the stroke stays in place as a transparent one (paintPanelEdge in chrome.js), so a
 * live theme switch repaints without resizing the frame.
 *
 * The ground shows for the moment before the iframe paints and behind a frame that failed to mount.
 * White is right on a light page and a flash on a dark one, so it follows the look too — the widget's
 * own dark surface, the value its pages are built on (--widget-surface).
 * @param {string} theme - 'dark' | 'light' | 'auto'
 * @param {string|null} tint - Soft variant of the accent, or null without a custom accent
 * @returns {{border: string, background: string, shadow: string}}
 */
export function panelChrome(theme, tint) {
    return isDarkTheme(theme)
        ? {
            border: tint || defaultPanelBorder,
            background: '#0f1116',
            shadow: '0 0 40px rgba(255,255,255,.10)'
        }
        : {
            border: 'transparent',
            background: '#fff',
            shadow: '0 12px 40px rgba(0,0,0,.22)'
        };
}

/**
 * The launcher's fill and foreground for the look it is standing in. Contractors may pick a second
 * accent for dark surfaces, and the launcher sits on the customer's page rather than in our widget —
 * so it follows that page's look like the bubble stack above it does.
 *
 * The gradient travels beside the fill rather than replacing it: painted as a background-IMAGE over the
 * flat accent, so the circle keeps a solid colour underneath and a browser that drops the gradient still
 * shows the brand. Empty string means "no gradient", which is also what clears one that was switched off.
 * Both far ends are resolved server-side (WidgetAccentPalette), so the rule that decides whether the end
 * is darker or lighter than the accent lives in exactly one place.
 * @param {{accent: string, contrast: string, accentDark: string, contrastDark: string,
 *          gradient: string|null, gradientDark: string|null}} options
 * @param {string} theme - 'dark' | 'light' | 'auto'
 * @returns {{background: string, color: string, gradient: string}}
 */
export function launcherColors(options, theme) {
    const dark = isDarkTheme(theme);

    return dark
        ? {
            background: options.accentDark,
            color: options.contrastDark,
            gradient: launcherGradient(options.accentDark, options.gradientDark),
            /* The halo is drawn from the look's own accent — the flat one, never the gradient's far end:
               the ring leaves the circle and has to read as the same colour the circle started in. */
            pulse: options.accentDark,
            dark: true
        }
        : {
            background: options.accent,
            color: options.contrast,
            gradient: launcherGradient(options.accent, options.gradient),
            pulse: options.accent,
            dark: false
        };
}

/**
 * The diagonal gradient over one accent, or '' where the contractor wants the flat circle.
 * 135deg — light falls from the top left on every other surface of this widget, so the circle is lit the
 * same way rather than banded top-to-bottom.
 * @param {string} accent - Validated #rrggbb accent of the look being painted
 * @param {string|null} gradientEnd - Validated far end, or null for a flat fill
 * @returns {string}
 */
function launcherGradient(accent, gradientEnd) {
    return gradientEnd
        ? 'linear-gradient(135deg, ' + accent + ' 0%, ' + gradientEnd + ' 100%)'
        : '';
}
